import ViewColumn from "@mui/icons-material/ViewColumn";
import { Box, Button, Checkbox, InputAdornment, Popover, Stack, TextField, Typography } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid-pro";
import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
	type ColumnVisibilityModel,
	resetGridColumnVisibility,
	selectColumnVisibilityForGrid,
	setGridColumnVisibility} from "../../../../redux/dataGridPrefsSlice";

// ─────────────────────────────────────────────────────────────────────────────
// CoAppColumnsButton — outlined pill that opens a checkbox-list popover for
// toggling column visibility. State is persisted to redux-persist via
// `dataGridPrefsSlice`, keyed by `gridKey`.
//
// Columns flagged as `hideable: false` (e.g. the rule-name or user identifier
// column) appear disabled in the popover so the user can never hide them and
// end up with an unparseable grid.
// ─────────────────────────────────────────────────────────────────────────────

interface CoAppColumnsButtonProps {
	gridKey: string;
	columns: GridColDef[];
	/** Optional override for the resource label (e.g. "rules", "users") shown next to the button. */
	resourceCount?: { count: number; label: string };
}

export default function CoAppColumnsButton({ gridKey, columns, resourceCount }: CoAppColumnsButtonProps) {
	const dispatch = useDispatch();
	const visibilityModel = useSelector(selectColumnVisibilityForGrid(gridKey));
	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const [search, setSearch] = useState("");

	const togglableColumns = useMemo(
		() =>
			columns.filter((col) => {
				if (col.field === "__check__") return false;
				if (col.field === "actions") return false;
				if (col.field === "id" || col.field === "uuid") return false;
				return true;
			}),
		[columns]
	);

	const filteredColumns = useMemo(() => {
		const q = search.trim().toLowerCase();
		if (!q) return togglableColumns;
		return togglableColumns.filter((col) =>
			((col.headerName as string) || col.field).toLowerCase().includes(q)
		);
	}, [togglableColumns, search]);

	const allVisible = togglableColumns.every((col) => visibilityModel[col.field] !== false);
	const someVisible = togglableColumns.some((col) => visibilityModel[col.field] !== false);

	const handleToggle = (field: string, hideable: boolean | undefined) => {
		if (hideable === false) return;
		const isVisible = visibilityModel[field] !== false;
		const next: ColumnVisibilityModel = { ...visibilityModel, [field]: !isVisible };
		dispatch(setGridColumnVisibility({ gridKey, model: next }));
	};

	const handleToggleAll = () => {
		const next: ColumnVisibilityModel = {};
		for (const col of togglableColumns) {
			if (col.hideable !== false) next[col.field] = !allVisible;
		}
		dispatch(setGridColumnVisibility({ gridKey, model: { ...visibilityModel, ...next } }));
	};

	const handleReset = () => {
		dispatch(resetGridColumnVisibility({ gridKey }));
	};

	const open = Boolean(anchorEl);

	return (
		<Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
			{resourceCount ? (
				<Typography variant="body2" sx={{ color: "rgba(13, 31, 48, 0.55)", fontWeight: 500, fontSize: 13 }}>
					{resourceCount.count} {resourceCount.label}
				</Typography>
			) : null}
			<Button
				size="small"
				variant="outlined"
				// Brand teal-to-blue gradient paints the icon strokes (via
				// the global SVG <linearGradient id="coapp-grad-primary">)
				// and the label text (via background-clip: text). Together
				// they match the chip styling used elsewhere in the app.
				startIcon={
					<ViewColumn
						sx={{
							fontSize: 16,
							"& path, & rect": { fill: "url(#coapp-grad-primary)" }
						}}
					/>
				}
				onClick={(e) => setAnchorEl(e.currentTarget)}
				sx={{
					borderRadius: 999,
					borderColor: "rgba(2, 213, 171, 0.55)",
					textTransform: "none",
					fontWeight: 600,
					fontSize: 13,
					px: 1.75,
					py: 0.5,
					height: 34,
					backgroundColor: "#FFFFFF",
					// Gradient label text. The Button's text node lives
					// inside a span; clip the gradient image to that text.
					"& .MuiButton-startIcon": { mr: 0.75 },
					"& > .MuiButton-startIcon ~ *, & > span:not(.MuiButton-startIcon)": {
						backgroundImage: "linear-gradient(135deg, #02D5AB 0%, #0A7FA8 100%)",
						WebkitBackgroundClip: "text",
						backgroundClip: "text",
						color: "transparent"
					},
					color: "transparent",
					backgroundImage: "linear-gradient(135deg, #02D5AB 0%, #0A7FA8 100%)",
					WebkitBackgroundClip: "text",
					backgroundClip: "text",
					"&:hover": {
						borderColor: "#02D5AB",
						backgroundColor: "rgba(2, 213, 171, 0.08)"
					}
				}}
			>
				Columns
			</Button>
			<Popover
				open={open}
				anchorEl={anchorEl}
				onClose={() => { setAnchorEl(null); setSearch(""); }}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "right" }}
				slotProps={{
					paper: {
						sx: {
							mt: 0.5,
							borderRadius: 2.5,
							boxShadow: "0 12px 30px rgba(13, 31, 48, 0.14), 0 2px 8px rgba(13, 31, 48, 0.08)",
							border: "1px solid rgba(13, 31, 48, 0.06)",
							p: 1.5,
							minWidth: 240
						}
					}
				}}
			>
				<Typography
					variant="caption"
					sx={{
						display: "block",
						color: "rgba(13, 31, 48, 0.45)",
						fontWeight: 700,
						letterSpacing: "0.10em",
						fontSize: 11,
						textTransform: "uppercase",
						pl: 1.25,
						pb: 1,
						pt: 0.5
					}}
				>
					Columns
				</Typography>
				<TextField
					size="small"
					placeholder="Search columns"
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					slotProps={{
						input: {
							startAdornment: (
								<InputAdornment position="start">
									<Box component="span" sx={{ fontSize: 14, color: "rgba(13, 31, 48, 0.4)", lineHeight: 1 }}>🔍</Box>
								</InputAdornment>
							)
						}
					}}
					sx={{
						mb: 0.75,
						"& .MuiOutlinedInput-root": {
							borderRadius: 2,
							fontSize: 13,
							"& fieldset": { borderColor: "rgba(13, 31, 48, 0.15)" },
							"&:hover fieldset": { borderColor: "rgba(2, 213, 171, 0.5)" },
							"&.Mui-focused fieldset": { borderColor: "#02D5AB" }
						}
					}}
				/>
				{/* Show/Hide All row */}
				<Box
					onClick={handleToggleAll}
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1,
						px: 1,
						py: 0.5,
						borderRadius: 1.5,
						cursor: "pointer",
						borderBottom: "1px solid rgba(13, 31, 48, 0.06)",
						mb: 0.5,
						"&:hover": { background: "rgba(2, 213, 171, 0.06)" }
					}}
				>
					<Checkbox
						checked={allVisible}
						indeterminate={!allVisible && someVisible}
						tabIndex={-1}
						disableRipple
						sx={{ p: 0.25 }}
					/>
					<Typography variant="body2" sx={{ fontSize: 13, color: "rgba(13, 31, 48, 0.86)", fontWeight: 600 }}>
						Show/Hide All
					</Typography>
				</Box>
				<Stack spacing={0.25}>
					{filteredColumns.map((col) => {
						const field = col.field;
						const headerName = (col.headerName as string) || field;
						const isLocked = col.hideable === false;
						const isVisible = visibilityModel[field] !== false;
						return (
							<Box
								key={field}
								onClick={() => handleToggle(field, col.hideable)}
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1,
									px: 1,
									py: 0.5,
									borderRadius: 1.5,
									cursor: isLocked ? "not-allowed" : "pointer",
									opacity: isLocked ? 0.4 : 1,
									"&:hover": {
										background: isLocked ? "transparent" : "rgba(2, 213, 171, 0.06)"
									}
								}}
							>
								<Checkbox
									checked={isVisible}
									disabled={isLocked}
									tabIndex={-1}
									disableRipple
									sx={{ p: 0.25 }}
								/>
								<Typography variant="body2" sx={{ fontSize: 13, color: "rgba(13, 31, 48, 0.86)", fontWeight: 500 }}>
									{headerName}
								</Typography>
							</Box>
						);
					})}
				</Stack>
				<Box
					sx={{
						display: "flex",
						justifyContent: "flex-end",
						pt: 1,
						mt: 0.5,
						borderTop: "1px solid rgba(13, 31, 48, 0.06)"
					}}
				>
					<Button
						size="small"
						onClick={handleReset}
						sx={{
							textTransform: "none",
							fontSize: 12,
							fontWeight: 600,
							color: "rgba(13, 31, 48, 0.5)",
							"&:hover": { color: "var(--coapp-text-interactive, #057A6D)", background: "rgba(2, 213, 171, 0.06)" }
						}}
					>
						Reset
					</Button>
				</Box>
			</Popover>
		</Stack>
	);
}
